import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  FileSpreadsheet,
  FileText,
  Plus,
  Printer,
  Search,
  Pencil,
  Trash2,
  Eye,
  UserPlus,
  FileBarChart2,
  TrendingUp,
  ReceiptText,
  CreditCard,
  ClipboardList,
  FileDigit,
  ArrowLeft,
  MessageCircle,
  Loader,
  X,
  CheckCircle,
  Calendar,
  Clock,
  User,
  BadgeCheck,
  SearchX,
  Sparkles,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { device } from "../../atoms/Utlis/media";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { getAllOrdersAPI, deleteOrderAPI, getOrderByIdAPI, shareOrderOnWhatsAppAPI, getOrderWorkAssignmentsAPI } from "../../../Redux/ApiCalls/Dashboard/OrderAPI";
import { createInvoiceFromOrderAPI } from "../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { usePermissions } from "../../../hooks/usePermissions";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { DocumentPrintTemplate } from "./DocumentPrintTemplate";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "./ListReportPrintTemplate";
import OrderShareModal from "./OrderShareModal";
import StudioTermsManageModal from "./StudioTermsManageModal";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  RemainingCell,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { SubmitButton } from "../../molecules/Dashboard/Member/memberFormStyles";
import {
  ModalHero,
  ModalHeroGlow,
  ModalHeroRow,
  ModalHeroText,
  ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  ModalActions,
  StatusConfirmOverlay,
  StatusConfirmBox,
  StatusBody,
  StatusCancelBtn,
  StatusConfirmBtn,
} from "./quotationMasterListShared";

type FunctionDetail = {
  date: string;
  session: string;
  events: string;
};

type OrderRecord = {
  id: string | number;
  orderNo: string;
  orderDate: string;
  customer: string;
  function_details?: string;
  functionDetails: FunctionDetail[];
  total: string;
  dueAmount: string;
  status: "Active" | "Completed" | "Cancelled" | "Pending";
  branch_id?: number | null;
  branch_name?: string | null;
};

const BASE_ACTION_BUTTONS = [
  { key: "view", icon: Eye, label: "View" },
  { key: "edit", icon: Pencil, label: "Edit" },
  { key: "assign", icon: UserPlus, label: "Assign Work" },
  // { key: "workReport",  icon: FileBarChart2, label: "Work Report" },
  // { key: "progress",    icon: TrendingUp,    label: "Progress" },
  // { key: "expense",     icon: ReceiptText,   label: "Order Expense" },
  // { key: "orderReport", icon: ClipboardList, label: "Order Report" },
  // { key: "payment",     icon: CreditCard,    label: "Make Payment" },
  { key: "createInvoice", icon: FileDigit, label: "Create Invoice" },
  { key: "share", icon: MessageCircle, label: "Share Order" },
  { key: "delete", icon: Trash2, label: "Delete" },
];

const AllOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { allOrders, totalCount, loading } = useAppSelector((state) => state.order);
  const { selectedBranch } = useAppSelector((state) => state.auth);

  const hasAnyOrderAction = hasAnyPermission([
    "orders.read",
    "orders.update",
    "orders.delete",
    "payments_management.create",
  ]);


  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState<{ id: string | number; orderNo: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | number | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [createInvoiceConfirmId, setCreateInvoiceConfirmId] = useState<number | string | null>(null);

  const { user, studio_logo, business } = useAppSelector((state) => state.auth);
  const [sharingOrderId, setSharingOrderId] = useState<string | number | null>(null);
  const [modalShareOrder, setModalShareOrder] = useState<any>(null);
  const [activeOrderForShare, setActiveOrderForShare] = useState<any>(null);
  const sharePrintRef = useRef<HTMLDivElement>(null);
  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  const [isStudioTermsModalOpen, setIsStudioTermsModalOpen] = useState(false);

  const hasStudioTerms = Boolean(business?.terms_and_conditions && business.terms_and_conditions.trim());

  // Work assignments map: orderId -> assignments[]
  const [workAssignmentsMap, setWorkAssignmentsMap] = useState<Map<number | string, any[]>>(new Map());
  const [viewAssignmentOrder, setViewAssignmentOrder] = useState<{ order: any; assignments: any[] } | null>(null);

  const handleShareOrder = async (orderId: string | number) => {
    try {
      const fullOrder = await getOrderByIdAPI(orderId);
      if (!fullOrder) {
        showErrorToast("Order not found");
        return;
      }
      setModalShareOrder(fullOrder);
    } catch (err: any) {
      showErrorToast("Failed to fetch order details");
    }
  };

  const handleOfficialShare = async () => {
    if (!modalShareOrder) return;
    setSharingOrderId(modalShareOrder.id);
    try {
      setActiveOrderForShare(modalShareOrder);

      // Give it a moment to render in the hidden div
      await new Promise((resolve) => setTimeout(resolve, 600));

      if (sharePrintRef.current) {
        const blob = await exportElementAsPDF(sharePrintRef.current);
        const filename = `Order_${modalShareOrder.order_no}.pdf`;
        const result = await shareOrderOnWhatsAppAPI(modalShareOrder.id, blob, filename);
        showSuccessToast(`Order sent to ${result.sent_to || modalShareOrder.customer_phone} on WhatsApp 🎉`);
      } else {
        showErrorToast("Print template not ready. Try again.");
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to share on WhatsApp.");
    } finally {
      setSharingOrderId(null);
      setActiveOrderForShare(null);
      setModalShareOrder(null);
    }
  };

  const fetchOrders = () => {
    dispatch(getAllOrdersAPI({ page: currentPage, page_size: entries, search: searchText, branch_id: selectedBranch?.id }));
  };

  // Fetch all work assignments (all pages) and build a map keyed by orderId
  const fetchAllWorkAssignments = async () => {
    try {
      let all: any[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await getOrderWorkAssignmentsAPI({ page, page_size: 100, branch_id: selectedBranch?.id });
        const list = res?.results || (Array.isArray(res) ? res : []);
        all = [...all, ...list];
        hasMore = !!(res?.next && list.length === 100);
        page += 1;
      }
      const map = new Map<number | string, any[]>();
      all.forEach((a: any) => {
        const key = a.order;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(a);
      });
      setWorkAssignmentsMap(map);
    } catch {
      // silently fail — doesn't block order list
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [dispatch, currentPage, entries, searchText, selectedBranch?.id]);

  // Re-fetch assignments whenever the orders page changes
  useEffect(() => {
    fetchAllWorkAssignments();
  }, [currentPage, entries, searchText, selectedBranch?.id]);

  const normalizedOrders = useMemo(() => {
    return (allOrders || []).map((o: any) => {
      // opt_service_total is returned from the list API (opt_album_page_price + opt_video_price)
      // It is informational only and NOT included in grand_total (backend fixed).
      // For existing orders created before the backend fix, we still subtract it if present.
      const optionalServicesSum = Number(o.opt_service_total || 0);
      const totalNum = Number(o.grand_total || o.total || 0);
      const dueNum = Number(o.due_amount || o.dueAmount || 0);
      const correctedTotal = Math.max(0, totalNum - optionalServicesSum).toFixed(2);
      const correctedDue = Math.max(0, dueNum - optionalServicesSum).toFixed(2);

      return {
        id: o.id,
        orderNo: o.order_no || o.orderNo || "",
        orderDate: o.order_date || o.orderDate || "",
        customer: o.customer_name || o.customer || "",
        total: correctedTotal,
        dueAmount: correctedDue,
        status: o.status || "Active",
        function_details: o.function_details || "",
        functionDetails: Array.isArray(o.functionDetails) ? o.functionDetails : [],
        branch_id: o.branch_id || null,
        branch_name: o.branch_name || null,
      };
    });
  }, [allOrders]);

  const handleDelete = (id: string | number, orderNo: string) => {
    setDeleteConfirmOrder({ id, orderNo });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmOrder) return;
    try {
      setDeleting(true);
      await deleteOrderAPI(deleteConfirmOrder.id);
      showSuccessToast("Order deleted successfully");
      fetchOrders();
    } catch (err) {
      showErrorToast("Failed to delete order");
    } finally {
      setDeleting(false);
      setDeleteConfirmOrder(null);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    copyDataToClipboard(
      normalizedOrders,
      ["Order No", "Order Date", "Customer", "Total", "Due Amount", "Status"],
      ["orderNo", "orderDate", "customer", "total", "dueAmount", "status"],
      "Orders list copied to clipboard"
    );
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToCSV(
      normalizedOrders,
      ["Order No", "Order Date", "Customer", "Total", "Due Amount", "Status"],
      ["orderNo", "orderDate", "customer", "total", "dueAmount", "status"],
      "orders_export.csv",
      "CSV exported successfully"
    );
  };

  const handleExportExcel = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToExcel(
      normalizedOrders,
      ["Order No", "Order Date", "Customer", "Total", "Due Amount", "Status"],
      ["orderNo", "orderDate", "customer", "total", "dueAmount", "status"],
      "orders_export.xls",
      "Excel exported successfully"
    );
  };

  const orderReportRows = useMemo(
    () =>
      normalizedOrders.map((o) => ({
        orderNo: o.orderNo,
        orderDate: o.orderDate,
        customer: o.customer,
        total: `₹${formatINR(o.total)}`,
        dueAmount: `₹${formatINR(o.dueAmount)}`,
        status: o.status,
        status_key: o.status,
        total_raw: Number(o.total) || 0,
        due_raw: Number(o.dueAmount) || 0,
      })),
    [normalizedOrders]
  );

  const orderReportSummary = useMemo(() => {
    const totalValue = orderReportRows.reduce((sum, row) => sum + row.total_raw, 0);
    const totalDue = orderReportRows.reduce((sum, row) => sum + row.due_raw, 0);
    return [
      { label: "Total Orders", value: String(orderReportRows.length) },
      { label: "Total Order Value", value: `₹${formatINR(totalValue)}` },
      { label: "Total Due Amount", value: `₹${formatINR(totalDue)}`, highlight: true },
    ];
  }, [orderReportRows]);

  const getReportStatusStyle = (status: string) =>
    statusColors[status] || { bg: "rgba(93,28,52,0.1)", color: "#5D1C34" };

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "orders_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visibleOrders = normalizedOrders;
  const totalPages = Math.ceil(totalCount / entries) || 1;
  const hasActiveSearch = !!searchText;

  const orderStats = useMemo(() => {
    const active = normalizedOrders.filter((o) => o.status === "Active").length;
    const completed = normalizedOrders.filter((o) => o.status === "Completed").length;
    const totalDue = normalizedOrders.reduce((sum, o) => sum + Number(o.dueAmount || 0), 0);
    const pageValue = normalizedOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
    return { active, completed, totalDue, pageValue };
  }, [normalizedOrders]);

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string | number) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[String(id)];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 320;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  const activeMenuOrder = useMemo(
    () => visibleOrders.find((o) => o.id === activeMenuId) ?? null,
    [visibleOrders, activeMenuId]
  );

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Studio orders
              </HeroBadge>
              <PageTitle className="linear-text">All Orders</PageTitle>
              <PageSubtitle>
                Manage studio bookings, events, payments, and order details.
              </PageSubtitle>
            </div>
          </HeroText>
          <PermissionGate permission="orders.create">
            <SubmitButton
              type="button"
              $ready
              onClick={() => navigate("/dashboard/orders/create")}
              style={{ minWidth: 180, height: 46 }}
            >
              <Plus size={16} /> Add order
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <StatsGrid>
        <WalletHero>
          <WalletIconWrap>
            <ClipboardList size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{totalCount}</WalletHeroValue>
            <WalletHeroLabel>Total orders</WalletHeroLabel>
          </WalletHeroBody>
        </WalletHero>

        <StatCard $tone="#31a66a">
          <StatIcon $tone="#31a66a">
            <CheckCircle size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{orderStats.active}</StatValue>
            <StatLabel>Active on page</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#35b7c8">
          <StatIcon $tone="#35b7c8">
            <BadgeCheck size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{orderStats.completed}</StatValue>
            <StatLabel>Completed on page</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#e44862">
          <StatIcon $tone="#e44862">
            <CreditCard size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>₹{formatINR(orderStats.totalDue)}</StatValue>
            <StatLabel>Due on page</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      <Panel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input value={searchVal} onChange={(e) => setSearchVal(e.target.value)} placeholder="Search orders" />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select value={entries} onChange={(e) => { setEntries(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <TermsManageButton
              type="button"
              onClick={() => setIsStudioTermsModalOpen(true)}
              $hasTerms={hasStudioTerms}
              title={hasStudioTerms ? "Edit Studio Terms & Conditions" : "Add Studio Terms & Conditions"}
            >
              {hasStudioTerms ? <Pencil size={13} /> : <Plus size={13} />}
              <span>{hasStudioTerms ? "Edit Terms & Condition" : "Add Terms & Condition"}</span>
            </TermsManageButton>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>


        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Order records</ListTitle>
          {!loading && (
            <CountBadge>
              {totalCount} record{totalCount !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="48px 22px 60px" />
        ) : visibleOrders.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveSearch ? <SearchX size={36} strokeWidth={1.5} /> : <ClipboardList size={36} strokeWidth={1.5} />}
            title={hasActiveSearch ? "No orders found" : "No orders yet"}
            description={
              hasActiveSearch
                ? "Nothing matched your search. Try a different order number, customer name, or keyword."
                : "Create your first order to start managing studio bookings, events, payments, and invoices."
            }
            actions={[
              ...(hasActiveSearch
                ? [{
                  label: "Clear search",
                  onClick: () => { setSearchVal(""); setSearchText(""); setCurrentPage(1); },
                  variant: "secondary" as const,
                }]
                : []),
              ...(hasPermission("orders.create")
                ? [{
                  label: hasActiveSearch ? "Add Order" : "Add Your First Order",
                  onClick: () => navigate("/dashboard/orders/create"),
                  icon: <Plus size={15} />,
                }]
                : []),
            ]}
            hint={!hasActiveSearch ? {
              icon: <Sparkles size={13} />,
              text: "Track events, payments, work assignments & invoices",
            } : undefined}
          />
        ) : (
          <PanelTableScroll ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={1100}>
                <thead>
                  <tr>
                    <th style={{ width: 48 }}><span>#</span></th>
                    <th><span>Order No</span></th>
                    <th><span>Order Date</span></th>
                    <th><span>Customer</span></th>
                    <th><span>Branch</span></th>
                    <th><span>Function Details</span></th>
                    <th><span>Total (₹)</span></th>
                    <th><span>Due Amount (₹)</span></th>
                    <th><span>Status</span></th>
                    {hasAnyOrderAction && <th style={{ textAlign: "right" }}><span>Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order, idx) => (
                    <tr key={order.id}>
                      <td style={{ color: "rgba(23,23,23,0.45)", fontWeight: 600 }}>
                        {(currentPage - 1) * entries + idx + 1}
                      </td>
                      <td><OrderNo>{order.orderNo}</OrderNo></td>
                      <td style={{ whiteSpace: "nowrap" }}>{order.orderDate}</td>
                      <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{order.customer}</td>
                      <td>
                        {order.branch_name ? (
                          <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                            {order.branch_name}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                      <td>
                        {order.function_details ? (
                          <div style={{ fontSize: 12, color: "rgba(23, 23, 23, 0.75)" }}>
                            {order.function_details}
                          </div>
                        ) : (
                          <FunctionList>
                            {order.functionDetails.map((f: any, i: number) => (
                              <FunctionItem key={i}>
                                <FunctionDate>{f.date}</FunctionDate>
                                <FunctionSession>{f.session}</FunctionSession>
                                <FunctionEvents>({f.events})</FunctionEvents>
                              </FunctionItem>
                            ))}
                          </FunctionList>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <AmountCell>₹{formatINR(order.total)}</AmountCell>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <RemainingCell>₹{formatINR(order.dueAmount)}</RemainingCell>
                      </td>
                      <td><StatusBadge $status={order.status}>{order.status}</StatusBadge></td>
                      {hasAnyOrderAction && (
                        <td style={{ textAlign: "right" }}>
                          <MenuContainer>
                            <ThreeDotsButton
                              ref={(el) => { menuButtonRefs.current[String(order.id)] = el; }}
                              type="button"
                              aria-expanded={activeMenuId === order.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleMenu(order.id);
                              }}
                            >
                              <Dot />
                              <Dot />
                              <Dot />
                            </ThreeDotsButton>
                          </MenuContainer>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </PanelTableScroll>
        )}

        {!loading && visibleOrders.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            Showing {totalCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalCount)} of {totalCount} entries
          </FooterText>
          <Pagination>
            <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton key={idx} $active={currentPage === idx + 1} onClick={() => setCurrentPage(idx + 1)}>{idx + 1}</PageButton>
            ))}
            <PageButton disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</PageButton>
          </Pagination>
        </FooterRow>
      </Panel>

      {activeMenuOrder && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const orderAssignments = workAssignmentsMap.get(activeMenuOrder.id) || [];
                const hasAssignment = orderAssignments.length > 0;
                const actionButtons = BASE_ACTION_BUTTONS
                  .filter((btn) => {
                    if (btn.key === "view" || btn.key === "share") return hasPermission("orders.read");
                    if (btn.key === "edit") return hasPermission("orders.update");
                    if (btn.key === "assign") return hasAssignment ? hasAnyPermission(["orders.read", "orders.update"]) : hasPermission("orders.update");
                    if (btn.key === "createInvoice") return hasAnyPermission(["invoice.create", "payments_management.create"]);
                    if (btn.key === "delete") return hasPermission("orders.delete");
                    return true;
                  })
                  .map((btn) =>
                    btn.key === "assign"
                      ? hasAssignment
                        ? { ...btn, icon: BadgeCheck, label: "View Assignment" }
                        : btn
                      : btn
                  );
                return actionButtons.map((btn) => {
                  const Icon = btn.icon;
                  return (
                    <MenuItem
                      key={btn.key}
                      type="button"
                      disabled={sharingOrderId !== null}
                      onClick={() => {
                        closeMenu();
                        if (btn.key === "delete") handleDelete(activeMenuOrder.id, activeMenuOrder.orderNo);
                        else if (btn.key === "edit") navigate(`/dashboard/orders/create?id=${activeMenuOrder.id}`);
                        else if (btn.key === "view") navigate(`/dashboard/orders/view?id=${activeMenuOrder.id}`);
                        else if (btn.key === "share") handleShareOrder(activeMenuOrder.id);
                        else if ((btn.key as string) === "payment") {
                          navigate(`/dashboard/orders/transactions?orderNo=${activeMenuOrder.orderNo}`);
                        } else if (btn.key === "createInvoice") {
                          setCreateInvoiceConfirmId(activeMenuOrder.id);
                        } else if (btn.key === "assign") {
                          if (hasAssignment) {
                            setViewAssignmentOrder({ order: activeMenuOrder, assignments: orderAssignments });
                          } else {
                            navigate(`/dashboard/orders/work?orderNo=${activeMenuOrder.orderNo}`);
                          }
                        } else {
                          showSuccessToast(`${(btn as any).label} — ${activeMenuOrder.orderNo}`);
                        }
                      }}
                    >
                      <ItemIconContainer $assigned={btn.key === "assign" && hasAssignment}>
                        {sharingOrderId === activeMenuOrder.id && btn.key === "share" ? (
                          <Loader size={14} className="spin" />
                        ) : (
                          <Icon size={14} />
                        )}
                      </ItemIconContainer>
                      <ItemLabel $assigned={btn.key === "assign" && hasAssignment}>
                        {sharingOrderId === activeMenuOrder.id && btn.key === "share"
                          ? "Sharing..."
                          : btn.label}
                      </ItemLabel>
                    </MenuItem>
                  );
                });
              })()}
            </DropdownMenu>
          </>,
          document.body
        )}

      {/* Delete confirmation modal */}
      {deleteConfirmOrder !== null && (
        <DeletePopup
          text={`Are you sure you want to delete order ${deleteConfirmOrder.orderNo}?`}
          buttonText="Delete"
          cancel={() => setDeleteConfirmOrder(null)}
          Delete={handleConfirmDelete}
        />
      )}

      {createInvoiceConfirmId !== null &&
        createPortal(
          <StatusConfirmOverlay onClick={() => !creatingInvoice && setCreateInvoiceConfirmId(null)}>
            <StatusConfirmBox onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <ModalHeroGlow />
                <ModalHeroRow>
                  <ModalHeroText>
                    <ModalHeroBadge>
                      <FileText size={11} />
                      Invoice draft
                    </ModalHeroBadge>
                    <ModalTitle>Create Invoice?</ModalTitle>
                    <ModalSubtitle>
                      A draft invoice will be created from this order&apos;s details.
                    </ModalSubtitle>
                  </ModalHeroText>
                  <CloseBtn
                    type="button"
                    onClick={() => setCreateInvoiceConfirmId(null)}
                    disabled={creatingInvoice}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </CloseBtn>
                </ModalHeroRow>
              </ModalHero>
              <ModalBody>
                <StatusBody>
                  Are you sure you want to create an invoice for order{" "}
                  <strong>
                    #
                    {normalizedOrders.find((o) => o.id === createInvoiceConfirmId)?.orderNo ||
                      createInvoiceConfirmId}
                  </strong>
                  ?
                </StatusBody>
              </ModalBody>
              <ModalActions>
                <StatusConfirmBtn
                  type="button"
                  disabled={creatingInvoice}
                  onClick={async () => {
                    const orderId = createInvoiceConfirmId;
                    if (orderId === null) return;
                    setCreatingInvoice(true);
                    try {
                      const res = await createInvoiceFromOrderAPI({ order_id: Number(orderId) });
                      showSuccessToast("Invoice drafted from order successfully");
                      navigate(`/dashboard/invoice/edit?id=${res.id}`);
                    } catch (err: any) {
                      const responseData = err?.response?.data;
                      const errorMsg = responseData?.message || responseData?.error?.message;
                      if (err?.response?.status === 400 || errorMsg?.includes("already exists")) {
                        showErrorToast("Invoice for this order is already created");
                      } else {
                        showErrorToast(errorMsg || "Failed to create invoice from order");
                      }
                    } finally {
                      setCreatingInvoice(false);
                      setCreateInvoiceConfirmId(null);
                    }
                  }}
                >
                  {creatingInvoice ? (
                    <>
                      <Loader size={16} className="spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      Create Invoice
                    </>
                  )}
                </StatusConfirmBtn>
                <StatusCancelBtn
                  type="button"
                  onClick={() => setCreateInvoiceConfirmId(null)}
                  disabled={creatingInvoice}
                >
                  Cancel
                </StatusCancelBtn>
              </ModalActions>
            </StatusConfirmBox>
          </StatusConfirmOverlay>,
          document.body
        )}

      {modalShareOrder && (
        <OrderShareModal
          order={modalShareOrder}
          onClose={() => setModalShareOrder(null)}
          onShareOfficial={handleOfficialShare}
          loading={sharingOrderId === modalShareOrder.id}
        />
      )}

      {activeOrderForShare && (
        <HiddenPrintContainer>
          <DocumentPrintTemplate
            type="order"
            activeDoc={activeOrderForShare}
            user={user}
            studioLogo={studio_logo || business?.studio_logo}
            studioName={business?.studio_name}
            studioAddress={business?.address}
            printRef={sharePrintRef}
          />
        </HiddenPrintContainer>
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Orders Report"
        subtitle="Studio bookings and order overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "orderNo", label: "Order No", width: "14%" },
          { key: "orderDate", label: "Date", width: "12%" },
          { key: "customer", label: "Customer", width: "22%" },
          {
            key: "total",
            label: "Total",
            align: "right",
            width: "14%",
            render: (value) => <CellBold>{String(value)}</CellBold>,
          },
          {
            key: "dueAmount",
            label: "Due Amount",
            align: "right",
            width: "14%",
            render: (value, row) => (
              <CellBold style={{ color: Number(row.due_raw) > 0 ? "#c93a55" : "#2d8a55" }}>
                {String(value)}
              </CellBold>
            ),
          },
          {
            key: "status",
            label: "Status",
            align: "center",
            width: "12%",
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.status_key || ""));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
        ]}
        rows={orderReportRows}
        summaryItems={orderReportSummary}
      />

      {/* View Assignment Modal */}
      {viewAssignmentOrder && (
        <AssignmentViewOverlay onClick={() => setViewAssignmentOrder(null)}>
          <AssignmentViewBox onClick={(e) => e.stopPropagation()}>
            <AssignmentViewHeader>
              <AssignmentViewTitleRow>
                <AssignmentViewIconBadge>
                  <BadgeCheck size={20} color="#fff" />
                </AssignmentViewIconBadge>
                <div>
                  <AssignmentViewTitle>Work Assignments</AssignmentViewTitle>
                  <AssignmentViewSubtitle>Order #{viewAssignmentOrder.order.orderNo}</AssignmentViewSubtitle>
                </div>
              </AssignmentViewTitleRow>
              <AssignmentViewClose onClick={() => setViewAssignmentOrder(null)}>
                <X size={18} />
              </AssignmentViewClose>
            </AssignmentViewHeader>

            <AssignmentViewDivider />

            <AssignmentScrollArea>
              {viewAssignmentOrder.assignments.map((a: any, idx: number) => (
                <AssignmentCard key={a.id ?? idx}>
                  <AssignmentCardHeader>
                    <AssignmentCardBadge $status={a.status}>{a.status || "Assigned"}</AssignmentCardBadge>
                    <AssignmentCardEventType>{a.event_type || "—"}</AssignmentCardEventType>
                  </AssignmentCardHeader>

                  <AssignmentCardGrid>
                    <AssignmentField>
                      <AssignmentFieldIcon><Calendar size={13} /></AssignmentFieldIcon>
                      <AssignmentFieldLabel>Date</AssignmentFieldLabel>
                      <AssignmentFieldValue>{a.event_date || "—"}</AssignmentFieldValue>
                    </AssignmentField>
                    <AssignmentField>
                      <AssignmentFieldIcon><Clock size={13} /></AssignmentFieldIcon>
                      <AssignmentFieldLabel>Session</AssignmentFieldLabel>
                      <AssignmentFieldValue>{a.event_session || "—"}</AssignmentFieldValue>
                    </AssignmentField>
                    <AssignmentField>
                      <AssignmentFieldIcon><User size={13} /></AssignmentFieldIcon>
                      <AssignmentFieldLabel>Role</AssignmentFieldLabel>
                      <AssignmentFieldValue>{a.role || "—"}</AssignmentFieldValue>
                    </AssignmentField>
                    <AssignmentField>
                      <AssignmentFieldIcon><CheckCircle size={13} /></AssignmentFieldIcon>
                      <AssignmentFieldLabel>Amount</AssignmentFieldLabel>
                      <AssignmentFieldValue>₹{formatINR(a.amount ?? 0)}</AssignmentFieldValue>
                    </AssignmentField>
                  </AssignmentCardGrid>

                  {/* Assigned team members */}
                  {(a.assigned_members || a.assignment_members || []).length > 0 && (
                    <AssignmentMembersSection>
                      <AssignmentMembersLabel>Assigned Members</AssignmentMembersLabel>
                      <AssignmentMembersList>
                        {(a.assigned_members || a.assignment_members || []).map((m: any, mi: number) => {
                          const emp = m.team_member_name || m.employee_name ||
                            (typeof m.team_member === "object" ? m.team_member?.name : null) ||
                            (typeof m.employee === "object" ? m.employee?.name : null) ||
                            `Member ${mi + 1}`;
                          return (
                            <AssignmentMemberChip key={mi}>
                              <User size={11} />
                              {emp}
                              {m.role && <AssignmentMemberRole>· {m.role}</AssignmentMemberRole>}
                            </AssignmentMemberChip>
                          );
                        })}
                      </AssignmentMembersList>
                    </AssignmentMembersSection>
                  )}

                  {a.employee_name && (
                    <AssignmentMembersSection>
                      <AssignmentMembersLabel>Assigned To</AssignmentMembersLabel>
                      <AssignmentMembersList>
                        {a.employee_name.split(",").map((name: string, ni: number) => (
                          <AssignmentMemberChip key={ni}>
                            <User size={11} />
                            {name.trim()}
                          </AssignmentMemberChip>
                        ))}
                      </AssignmentMembersList>
                    </AssignmentMembersSection>
                  )}
                </AssignmentCard>
              ))}
            </AssignmentScrollArea>
          </AssignmentViewBox>
        </AssignmentViewOverlay>
      )}

      <StudioTermsManageModal
        isOpen={isStudioTermsModalOpen}
        onClose={() => setIsStudioTermsModalOpen(false)}
      />
    </PageContainer>
  );
};

export default AllOrdersPage;

// Styled Components

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%);
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`;

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 0 22px;
  flex-shrink: 0;
`;

const PanelTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 20px;
  padding: 20px 22px;
  flex-wrap: wrap;

  @media ${device.tablet} {
    flex-direction: column;
    align-items: stretch;
  }
`;





const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.28);
  }
`;







const actionTooltipStyles = `
  position: relative;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 5px 10px;
    border-radius: 6px;
    background: #171717;
    color: #ffffff;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    z-index: 20;
    box-shadow: 0 4px 12px rgba(23, 23, 23, 0.18);
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;



const Divider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.08);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px 10px;
  flex-shrink: 0;
  @media ${device.tablet} { flex-direction: column; align-items: stretch; }
`;

const EntriesControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.72);
  select {
    height: 34px;
    border: 1px solid rgba(23, 23, 23, 0.12);
    border-radius: 8px;
    padding: 0 10px;
    background: #fff;
    color: #171717;
  }
`;

const SearchControl = styled.label`
  width: min(100%, 300px);
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  color: rgba(23, 23, 23, 0.42);
  background: #fafafa;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus-within {
    border-color: rgba(201, 77, 170, 0.35);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.08);
    background: #fff;
  }
  input {
    width: 100%;
    border: none;
    outline: none;
    font-family: Montserrat, sans-serif;
    font-size: 13px;
    color: #171717;
    background: transparent;
  }
`;

const ClearButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.42);
  padding: 0;
  &:hover {
    color: #171717;
  }
`;

const ExportRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 22px 14px;
  flex-wrap: wrap;
`;

const exportColors = { copy: "#8e4e8f", csv: "#35b7c8", excel: "#31a66a", pdf: "#e44862", print: "#f09a3e" };

const ExportButton = styled.button<{ $variant: keyof typeof exportColors }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: ${(p) => exportColors[p.$variant]};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const OrderNo = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const FunctionList = styled.ul`
  list-style: disc;
  margin: 0;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const FunctionItem = styled.li`
  font-size: 12px;
  line-height: 1.5;
  color: rgba(23, 23, 23, 0.75);
`;

const FunctionDate = styled.span`
  font-weight: 600;
  color: rgba(23, 23, 23, 0.85);
  margin-right: 4px;
`;

const FunctionSession = styled.span`
  color: #c94daa;
  font-weight: 600;
  margin-right: 3px;
`;

const FunctionEvents = styled.span`
  color: rgba(23, 23, 23, 0.6);
`;

const statusColors: Record<string, { bg: string; color: string }> = {
  Active: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Completed: { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  Pending: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  Cancelled: { bg: "rgba(228,72,98,0.12)", color: "#e44862" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => statusColors[p.$status]?.bg ?? "rgba(23,23,23,0.08)"};
  color:      ${(p) => statusColors[p.$status]?.color ?? "#171717"};
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 320px;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  color: #ffffff;
  background: ${gradient};
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  flex: 0 0 auto;
  transition: opacity 0.15s ease, transform 0.1s ease;
  ${actionTooltipStyles}

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 22px 22px;
  flex-shrink: 0;
  @media ${device.tablet} { flex-direction: column; align-items: flex-start; }
`;

const FooterText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  border-radius: 10px;
  padding: 0 12px;
  background: ${(p) => (p.$active ? gradient : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23,23,23,0.65)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 12px rgba(201, 77, 170, 0.22)" : "none")};

  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: ${(p) => (p.$active ? "#fff" : "#c94daa")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const HiddenPrintContainer = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  width: 800px;
  height: auto;
  overflow: hidden;
  background: white;
  pointer-events: none;
`;

// ─── View Assignment Modal Styles ────────────────────────────────────────────

const AssignmentViewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const AssignmentViewBox = styled.div`
  background: #ffffff;
  border-radius: 20px;
  width: min(100%, 560px);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const AssignmentViewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  flex-shrink: 0;
`;

const AssignmentViewTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const AssignmentViewIconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(144.01deg, #31a66a 0%, #1a7a4a 130%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(49, 166, 106, 0.25);
`;

const AssignmentViewTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const AssignmentViewSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const AssignmentViewClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  transition: all 0.15s ease;
  &:hover {
    background: #f5f5f5;
    color: #171717;
  }
`;

const AssignmentViewDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.08);
  flex-shrink: 0;
`;

const AssignmentScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const assignmentStatusColors: Record<string, { bg: string; color: string }> = {
  Assigned: { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  "In Progress": { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
  Completed: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  Pending: { bg: "rgba(23,23,23,0.08)", color: "rgba(23,23,23,0.6)" },
};

const AssignmentCard = styled.div`
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 14px;
  padding: 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.15s ease;
  &:hover {
    box-shadow: 0 4px 16px rgba(23, 23, 23, 0.07);
  }
`;

const AssignmentCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const AssignmentCardBadge = styled.span<{ $status?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => assignmentStatusColors[p.$status || ""]?.bg ?? "rgba(23,23,23,0.08)"};
  color: ${(p) => assignmentStatusColors[p.$status || ""]?.color ?? "rgba(23,23,23,0.6)"};
`;

const AssignmentCardEventType = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #c94daa;
`;

const AssignmentCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const AssignmentField = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.07);
  border-radius: 8px;
  padding: 8px 10px;
`;

const AssignmentFieldIcon = styled.span`
  color: rgba(23, 23, 23, 0.4);
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const AssignmentFieldLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  flex-shrink: 0;
`;

const AssignmentFieldValue = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #171717;
  margin-left: auto;
`;

const AssignmentMembersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AssignmentMembersLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const AssignmentMembersList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const AssignmentMemberChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(49, 166, 106, 0.1);
  color: #1a7a4a;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
`;

const AssignmentMemberRole = styled.span`
  color: rgba(23, 23, 23, 0.5);
  font-weight: 500;
`;

const AssignmentViewFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(23, 23, 23, 0.08);
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const AssignmentEditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%);
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: opacity 0.15s ease, transform 0.1s ease;
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const TermsManageButton = styled.button<{ $hasTerms?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.3)" : "rgba(23, 23, 23, 0.12)")};
  background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.05)" : "#ffffff")};
  color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "#171717")};
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.1)" : "#f8f9fa")};
    border-color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "rgba(23, 23, 23, 0.2)")};
    transform: translateY(-1px);
  }
`;